import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { ClaudeConnection } from '@aeris/contracts'
import {
  Button,
  ConfirmDialog,
  CopyableValue,
  QueryState,
  Section,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import { disconnectClaude, fetchClaudeConnections } from '../lib/api.ts'
import { formatDayTime } from '../lib/date-format.ts'

/**
 * O Claude conectado ao Aeris (F253).
 *
 * Em "Meu assistente", a pessoa vê a URL para colar e o passo a passo, e as
 * conexões dela. Em "Assistente" (dono, admin), as de todo mundo: é a conta da
 * loja sendo lida por fora, e quem administra precisa poder cortar.
 */
export const ClaudeConnections = ({
  everyone,
}: {
  /** Mostra quem conectou (a tela do administrador). */
  readonly everyone: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const connections = useQuery({
    queryKey: ['claude-connections'],
    queryFn: fetchClaudeConnections,
  })
  const [removing, setRemoving] = useState<ClaudeConnection | null>(null)

  const disconnect = useMutation({
    mutationFn: async (id: string) => {
      await disconnectClaude(id)
    },
    onSuccess: async () => {
      notify.success(
        'Claude desconectado. Ele perde o acesso na próxima pergunta.'
      )
      setRemoving(null)
      await queryClient.invalidateQueries({ queryKey: ['claude-connections'] })
    },
    // Sem onError: a rede de segurança do cache de mutações avisa (F248).
  })

  const list = connections.data?.connections ?? []

  return (
    <Section
      title="Claude"
      description={
        everyone
          ? 'Quem conectou o Claude a esta loja. Desconectar corta o acesso na hora.'
          : 'Conecte o Claude ao Aeris para perguntar os números da loja e fazer estudos. Ele só lê, e só o que o seu acesso já vê.'
      }
    >
      <QueryState
        isPending={connections.isPending}
        isError={connections.isError}
        error={connections.error}
        onRetry={() => void connections.refetch()}
        what="as conexões do Claude"
        isEmpty={false}
        empty={null}
      >
        <div className="flex flex-col gap-4">
          {everyone ? null : (
            <div className="flex flex-col gap-2">
              <CopyableValue
                label="Endereço"
                value={connections.data?.mcpUrl ?? ''}
                what="o endereço"
              />
              <ol className="list-decimal pl-5 text-(length:--text-sm) text-(--text-secondary)">
                <li>
                  No Claude, abra Configurações → Conectores → Adicionar
                  conector personalizado.
                </li>
                <li>Cole o endereço acima e clique em Conectar.</li>
                <li>
                  Entre com o seu e-mail e senha do Aeris e aprove. No Claude
                  Code:{' '}
                  <code>
                    claude mcp add --transport http aeris{' '}
                    {connections.data?.mcpUrl ?? ''}
                  </code>
                </li>
              </ol>
            </div>
          )}

          {list.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              {everyone
                ? 'Ninguém conectou o Claude a esta loja.'
                : 'Você ainda não conectou o Claude.'}
            </p>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {everyone ? <TableHeaderCell>Quem</TableHeaderCell> : null}
                    <TableHeaderCell>Aplicativo</TableHeaderCell>
                    <TableHeaderCell>Conectado em</TableHeaderCell>
                    <TableHeaderCell>Último uso</TableHeaderCell>
                    <TableHeaderCell>
                      <span className="sr-only">Ações</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((connection) => (
                    <TableRow key={connection.id}>
                      {everyone ? (
                        <TableCell>
                          {connection.userName ?? '—'}
                          {connection.mine ? ' (você)' : ''}
                        </TableCell>
                      ) : null}
                      <TableCell>{connection.clientName}</TableCell>
                      <TableCell>
                        {formatDayTime(connection.createdAt)}
                      </TableCell>
                      <TableCell>
                        {connection.lastUsedAt === null
                          ? 'nunca'
                          : formatDayTime(connection.lastUsedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setRemoving(connection)
                          }}
                        >
                          Desconectar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </QueryState>

      <ConfirmDialog
        open={removing !== null}
        title="Desconectar o Claude?"
        description={
          removing === null
            ? ''
            : `${removing.clientName}${everyone && removing.userName !== null ? ` de ${removing.userName}` : ''} perde o acesso aos números da loja na próxima pergunta. Para voltar, é só conectar de novo.`
        }
        confirmLabel="Desconectar"
        tone="danger"
        loading={disconnect.isPending}
        onConfirm={async () => {
          if (removing !== null) await disconnect.mutateAsync(removing.id)
        }}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
      />
    </Section>
  )
}
